import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './BreathingExercise.css';
import Icon from './components/ui/Icon';
import PastelButton from './components/ui/PastelButton';

// Get time-of-day based tint
const getTimeOfDayTint = () => {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 12) {
    return 'from-sky-100/20 via-blue-50/10 to-cyan-50/15';
  } else if (hour >= 12 && hour < 17) {
    return 'from-amber-50/15 via-yellow-50/10 to-orange-50/15';
  } else if (hour >= 17 && hour < 21) {
    return 'from-orange-100/20 via-rose-50/15 to-pink-50/20';
  } else {
    return 'from-indigo-100/20 via-purple-50/15 to-violet-50/20';
  }
};

// Duration Pill Component
const DurationPill = ({ option, isSelected, hasSelection, onClick }) => {
  return (
    <motion.button
      onClick={onClick}
      className={`
        relative px-5 py-3 rounded-full border-2 backdrop-blur-sm
        transition-colors duration-200 font-medium text-base
        ${isSelected 
          ? 'bg-white/50 border-white/60 text-gray-700 shadow-lg' 
          : hasSelection
            ? 'bg-white/20 border-white/25 text-gray-600 opacity-80 hover:opacity-100'
            : 'bg-white/25 border-white/30 text-gray-600 hover:bg-white/35'
        }
      `}
      style={{
        boxShadow: isSelected 
          ? '0 8px 20px -4px rgba(0,0,0,0.1), 0 0 15px -3px rgba(134, 239, 172, 0.4)'
          : '0 4px 12px -2px rgba(0,0,0,0.06)',
      }}
      whileHover={{ y: -2, boxShadow: '0 10px 25px -5px rgba(0,0,0,0.12)' }}
      whileTap={{ scale: 0.96 }}
      transition={{ type: 'spring', stiffness: 400, damping: 25 }}
    >
      {/* Selected glow ring */}
      {isSelected && (
        <motion.div
          className="absolute inset-0 rounded-full"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          style={{
            boxShadow: '0 0 0 2px rgba(255,255,255,0.7), 0 0 20px 3px rgba(134, 239, 172, 0.3)',
          }}
        />
      )}
      <span className="relative z-10">{option.label}</span>
    </motion.button>
  );
};

const BreathingExercise = ({ petName, petId, angelImage, onClose, onComplete }) => {
  const [currentPhase, setCurrentPhase] = useState('setup'); // setup, breathing, completed
  const [breathingState, setBreathingState] = useState('inhale'); // inhale, hold, exhale
  const [cycleCount, setCycleCount] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(60); // Default 1 minute
  const [selectedDuration, setSelectedDuration] = useState(60);
  const [phaseTimeLeft, setPhaseTimeLeft] = useState(4); // For current breathing phase
  const [isActive, setIsActive] = useState(false);
  const [isTimerSetup, setIsTimerSetup] = useState(false);

  const intervalRef = useRef(null);
  const phaseIntervalRef = useRef(null);
  const currentBreathingStateRef = useRef('inhale');
  const lastTransitionTimeRef = useRef(0);

  const timeOfDayTint = useMemo(() => getTimeOfDayTint(), []);

  // Get backend URL from environment
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  // Breathing pattern: 4 seconds inhale, 2 seconds hold, 6 seconds exhale
  const breathingPattern = {
    inhale: 4,
    hold: 2,
    exhale: 6
  };

  // Scale values for the breathing circle animation
  const minScale = 0.3;
  const maxScale = 1.0;

  const durationOptions = [
    { value: 60, label: '1:00' },
    { value: 120, label: '2:00' },
    { value: 180, label: '3:00' },
    { value: 300, label: '5:00' }
  ];

  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (phaseIntervalRef.current) clearInterval(phaseIntervalRef.current);
    };
  }, []);

  const startBreathing = () => {
    if (isTimerSetup) return;
    
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (phaseIntervalRef.current) clearInterval(phaseIntervalRef.current);
    
    setIsTimerSetup(true);
    setCurrentPhase('breathing');
    setTimeRemaining(selectedDuration);
    setIsActive(true);
    setBreathingState('inhale');
    currentBreathingStateRef.current = 'inhale';
    lastTransitionTimeRef.current = Date.now();
    setPhaseTimeLeft(breathingPattern.inhale);
    setCycleCount(0);
    
    intervalRef.current = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          completeSession();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    startBreathingPhase();
  };

  const startBreathingPhase = () => {
    if (phaseIntervalRef.current) {
      clearInterval(phaseIntervalRef.current);
    }
    phaseIntervalRef.current = setInterval(() => {
      setPhaseTimeLeft(prev => {
        if (prev <= 0.1) {
          nextBreathingPhase();
          return 0;
        }
        return prev - 0.1;
      });
    }, 100);
  };

  const nextBreathingPhase = () => {
    const now = Date.now();
    const timeSinceLastTransition = now - lastTransitionTimeRef.current;
    
    if (timeSinceLastTransition < 100) return;
    
    lastTransitionTimeRef.current = now;
    setBreathingState(prevState => {
      let nextState;
      let nextDuration;
      
      switch (prevState) {
        case 'inhale':
          nextState = 'hold';
          nextDuration = breathingPattern.hold;
          break;
        case 'hold':
          nextState = 'exhale';
          nextDuration = breathingPattern.exhale;
          break;
        case 'exhale':
          nextState = 'inhale';
          nextDuration = breathingPattern.inhale;
          setCycleCount(prev => prev + 1);
          break;
        default:
          nextState = 'inhale';
          nextDuration = breathingPattern.inhale;
      }
      
      currentBreathingStateRef.current = nextState;
      setPhaseTimeLeft(nextDuration);
      return nextState;
    });
  };

  const completeSession = async () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (phaseIntervalRef.current) clearInterval(phaseIntervalRef.current);
    
    setIsActive(false);
    setIsTimerSetup(false);
    setCurrentPhase('completed');

    try {
      await fetch(`${BACKEND_URL}/api/pets/${petId}/breathing?duration=${selectedDuration}&completed=true`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
    } catch (error) {
      console.error('Error saving breathing session:', error);
    }

    onComplete && onComplete();
  };

  const pauseSession = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (phaseIntervalRef.current) clearInterval(phaseIntervalRef.current);
    setIsActive(false);
    setIsTimerSetup(false);
  };

  const resumeSession = () => {
    if (isTimerSetup) return;
    
    setIsActive(true);
    setIsTimerSetup(true);
    
    intervalRef.current = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          completeSession();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    startBreathingPhase();
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getCircleScale = () => {
    const progress = (breathingPattern[breathingState] - phaseTimeLeft) / breathingPattern[breathingState];
    
    switch (breathingState) {
      case 'inhale':
        return minScale + (maxScale - minScale) * progress;
      case 'hold':
        return maxScale;
      case 'exhale':
        return maxScale - (maxScale - minScale) * progress;
      default:
        return minScale;
    }
  };

  const getInstructionText = () => {
    switch (breathingState) {
      case 'inhale': return 'Breathe In';
      case 'hold': return 'Hold';
      case 'exhale': return 'Breathe Out';
      default: return 'Breathe';
    }
  };

  const getBreathingColor = () => {
    switch (breathingState) {
      case 'inhale': return { ring: 'rgba(134, 239, 172, 0.8)', glow: 'rgba(134, 239, 172, 0.4)', text: '#4ade80' };
      case 'hold': return { ring: 'rgba(253, 224, 71, 0.8)', glow: 'rgba(253, 224, 71, 0.4)', text: '#facc15' };
      case 'exhale': return { ring: 'rgba(147, 197, 253, 0.8)', glow: 'rgba(147, 197, 253, 0.4)', text: '#93c5fd' };
      default: return { ring: 'rgba(255,255,255,0.6)', glow: 'rgba(255,255,255,0.3)', text: '#9ca3af' };
    }
  };

  const renderSetupScreen = () => (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="flex flex-col items-center w-full max-w-xl"
    >
      {/* Glass Panel */}
      <div className="relative bg-white/20 backdrop-blur-md rounded-3xl px-1.5 sm:px-3 pt-3 pb-6 shadow-[0_12px_30px_rgba(0,0,0,0.08)] border border-white/30 w-full max-w-xl">
        {/* Inner highlight */}
        <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-white/30 via-white/10 to-transparent pointer-events-none" />
        
        {/* Avatar with breathing animation */}
        <motion.div 
          className="relative mx-auto mb-6 w-36 h-36"
          animate={{ scale: [1, 1.03, 1] }}
          transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
          style={{ transformOrigin: 'center center' }}
        >
          {/* Radial glow behind avatar */}
          <div 
            className="absolute inset-0 rounded-full"
            style={{
              background: 'radial-gradient(circle, rgba(168, 85, 247, 0.2) 0%, rgba(236, 72, 153, 0.1) 50%, transparent 70%)',
              transform: 'scale(1.3)',
            }}
            aria-hidden="true"
          />
          {/* Glass frame */}
          <div className="absolute inset-0 rounded-full bg-white/30 border-4 border-white/40 shadow-xl" />
          <img 
            src={angelImage} 
            alt={`Angel ${petName}`} 
            className="relative z-10 w-full h-full rounded-full object-cover"
          />
        </motion.div>

        {/* Duration selector */}
        <div className="relative z-10 text-center mb-6">
          <h3 className="text-gray-700 font-medium text-base mb-4">Choose your session length</h3>
          <div className="flex gap-2 justify-center flex-wrap">
            {durationOptions.map((option) => (
              <DurationPill
                key={option.value}
                option={option}
                isSelected={selectedDuration === option.value}
                hasSelection={true}
                onClick={() => setSelectedDuration(option.value)}
              />
            ))}
          </div>
        </div>

        {/* Begin button */}
        <div className="relative z-10 flex justify-center">
          <motion.div
            whileHover={{ y: -2, scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <PastelButton onClick={startBreathing} color="mint" className="px-8">
              <Icon name="sparkle" size="sm" color="#fff" />
              Breathe
            </PastelButton>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );

  const renderBreathingScreen = () => {
    const colors = getBreathingColor();
    const scale = getCircleScale();
    
    return (
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="flex flex-col items-center w-full max-w-xl"
      >
        {/* Glass Panel */}
        <div className="relative bg-white/20 backdrop-blur-md rounded-3xl px-1.5 sm:px-3 pt-3 pb-6 shadow-[0_16px_40px_rgba(0,0,0,0.10)] border border-white/30 w-full max-w-xl">
          {/* Inner highlight */}
          <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-white/30 via-white/10 to-transparent pointer-events-none" />
          
          {/* Timer - Hero element */}
          <motion.div 
            className="relative z-10 text-center mb-4"
            key={timeRemaining}
          >
            <div className="inline-block bg-white/30 backdrop-blur-sm rounded-2xl px-6 py-2 border border-white/30">
              <span className="text-4xl md:text-5xl font-light text-gray-700 tracking-wide">
                {formatTime(timeRemaining)}
              </span>
            </div>
          </motion.div>

          {/* Breathing Ring */}
          <div className="relative z-10 flex justify-center mb-4">
            <div className="relative w-56 h-56 md:w-64 md:h-64">
              {/* Outer ring - static */}
              <div 
                className="absolute inset-0 rounded-full border-[3px]"
                style={{
                  borderColor: 'rgba(255,255,255,0.3)',
                  boxShadow: '0 0 30px rgba(255,255,255,0.1), inset 0 0 20px rgba(255,255,255,0.05)',
                }}
              />
              
              {/* Inner depth ring */}
              <div 
                className="absolute inset-3 rounded-full border-[2px]"
                style={{
                  borderColor: 'rgba(255,255,255,0.15)',
                }}
                aria-hidden="true"
              />
              
              {/* Animated breathing ring */}
              <motion.div 
                className="absolute inset-0 rounded-full border-[4px] flex items-center justify-center"
                style={{
                  borderColor: colors.ring,
                  boxShadow: `0 0 40px ${colors.glow}, inset 0 0 30px rgba(255,255,255,0.1)`,
                }}
                animate={{ 
                  scale: scale,
                  boxShadow: breathingState === 'inhale' 
                    ? `0 0 60px ${colors.glow}, inset 0 0 40px rgba(255,255,255,0.15)`
                    : `0 0 30px ${colors.glow}, inset 0 0 20px rgba(255,255,255,0.1)`
                }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
              />
              
              {/* Angel image in center */}
              <motion.div 
                className="absolute inset-0 flex items-center justify-center"
                animate={{ scale: 0.95 + (scale * 0.05) }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
              >
                <img 
                  src={angelImage} 
                  alt={`Angel ${petName}`}
                  className="w-24 h-24 md:w-28 md:h-28 rounded-full object-cover shadow-lg border-2 border-white/50"
                />
              </motion.div>
            </div>
          </div>

          {/* Breathing Instruction */}
          <div className="relative z-10 text-center mb-5">
            <AnimatePresence mode="wait">
              <motion.div
                key={breathingState}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
                className="inline-block bg-white/35 backdrop-blur-sm rounded-2xl px-5 py-2 border border-white/30"
              >
                <h3 
                  className="text-2xl font-medium mb-1"
                  style={{ color: colors.text }}
                >
                  {getInstructionText()}
                </h3>
                <div className="text-xl text-gray-500 font-light">
                  {Math.ceil(phaseTimeLeft)}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Controls */}
          <div className="relative z-10 flex justify-center gap-3 mb-4">
            {isActive ? (
              <motion.button 
                onClick={pauseSession}
                className="px-6 py-2.5 bg-white/40 backdrop-blur-sm text-gray-600 rounded-xl font-medium border border-white/30 shadow-sm"
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.96 }}
              >
                Pause
              </motion.button>
            ) : (
              <motion.button 
                onClick={resumeSession}
                className="px-6 py-2.5 bg-emerald-100/60 backdrop-blur-sm text-emerald-700 rounded-xl font-medium border border-emerald-200/40 shadow-sm"
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.96 }}
              >
                Resume
              </motion.button>
            )}
            <motion.button 
              onClick={onClose}
              className="px-6 py-2.5 bg-white/30 backdrop-blur-sm text-gray-500 rounded-xl font-medium border border-white/25 shadow-sm hover:bg-red-50/50 hover:text-red-500 hover:border-red-200/40"
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.96 }}
            >
              Stop
            </motion.button>
          </div>

          {/* Session Info */}
          <div className="relative z-10 text-center">
            <span className="text-sm text-gray-500 bg-white/25 backdrop-blur-sm px-4 py-1.5 rounded-full border border-white/20">
              Cycles completed: {cycleCount}
            </span>
          </div>
        </div>
      </motion.div>
    );
  };

  const renderCompletedScreen = () => (
    <motion.div 
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="flex flex-col items-center w-full max-w-xl"
    >
      {/* Glass Panel */}
      <div className="relative bg-white/22 backdrop-blur-md rounded-3xl px-1.5 sm:px-3 pt-3 pb-6 shadow-[0_16px_40px_rgba(0,0,0,0.10)] border border-white/30 w-full max-w-xl">
        {/* Inner highlight */}
        <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-white/30 via-white/10 to-transparent pointer-events-none" />
        
        {/* Stars */}
        <div className="relative z-10 flex justify-center gap-3 mb-4">
          {[0, 1, 2].map((i) => (
            <motion.span 
              key={i}
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ delay: i * 0.15, type: 'spring', stiffness: 200 }}
            >
              <Icon name="star" size={i === 1 ? "lg" : "md"} color="#fbbf24" />
            </motion.span>
          ))}
        </div>

        {/* Completion message */}
        <div className="relative z-10 text-center mb-5">
          <h2 className="text-2xl font-bold text-gray-700 mb-2">Session Complete!</h2>
          <p className="text-gray-600 text-sm leading-relaxed">
            You've taken {formatTime(selectedDuration)} of peaceful breaths with Angel {petName}.
          </p>
        </div>

        {/* Affirmation card */}
        <div className="relative z-10 bg-white/40 backdrop-blur-sm rounded-2xl p-4 border border-white/30 mb-5">
          <div className="flex justify-center mb-2">
            <Icon name="heart" size="lg" color="#ec4899" />
          </div>
          <h3 className="text-lg font-semibold text-gray-700 text-center mb-2">You are loved and supported</h3>
          <p className="text-sm text-gray-600 text-center leading-relaxed">
            Angel {petName} is proud of you for taking time to breathe and heal. 
            Each breath brings you closer to peace.
          </p>
        </div>

        {/* Stats */}
        <div className="relative z-10 flex justify-center gap-8 mb-5">
          <div className="text-center">
            <div className="text-3xl font-light text-gray-700">{cycleCount}</div>
            <div className="text-xs text-gray-500 uppercase tracking-wide">Cycles</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-light text-gray-700">{formatTime(selectedDuration)}</div>
            <div className="text-xs text-gray-500 uppercase tracking-wide">Time</div>
          </div>
        </div>

        {/* Continue button */}
        <div className="relative z-10 flex justify-center">
          <motion.div
            whileHover={{ y: -2, scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <PastelButton onClick={onClose} color="mint" className="px-6">
              Continue Journey
            </PastelButton>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="breathe-page-bg heaven-background relative min-h-screen">
      {/* Grain/noise overlay */}
      <div 
        className="absolute inset-0 pointer-events-none opacity-[0.03] z-0"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
        }}
        aria-hidden="true"
      />
      
      {/* Time-of-day tint */}
      <div className={`absolute inset-0 pointer-events-none bg-gradient-to-br ${timeOfDayTint} z-0`} aria-hidden="true" />
      
      {/* Radial glow behind content */}
      <div 
        className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 pointer-events-none z-0"
        style={{
          background: 'radial-gradient(circle, rgba(134, 239, 172, 0.15) 0%, rgba(168, 85, 247, 0.08) 40%, transparent 70%)',
        }}
        aria-hidden="true"
      />

      <div className="container min-h-[calc(100vh-96px)] sm:p-4 relative z-10">
        {/* Header */}
        <div className="relative text-center mb-6 pt-10">
          <motion.button
            onClick={onClose}
            className="absolute top-0 left-0 bg-white/70 backdrop-blur-md text-gray-600 px-4 py-2 rounded-xl font-medium border border-white/40 shadow-md text-sm"
            whileHover={{ y: -2, backgroundColor: 'rgba(255,255,255,0.85)' }}
            whileTap={{ scale: 0.98 }}
          >
            ← Back
          </motion.button>
        
          {currentPhase === 'setup' && (
            <h1 className="text-xl md:text-2xl font-bold text-gray-700 tracking-tight flex items-center justify-center gap-2">
              <Icon name="sparkle" size="md" color="primary" animate />
              Breathe with Angel {petName || 'Pet'}
            </h1>
          )}
        </div>

        {/* Content */}
        <div className="flex justify-center px-0.5 sm:px-4 pb-8">
          <AnimatePresence mode="wait">
            {currentPhase === 'setup' && renderSetupScreen()}
            {currentPhase === 'breathing' && renderBreathingScreen()}
            {currentPhase === 'completed' && renderCompletedScreen()}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default BreathingExercise;
